/**
 * The sanitizing markdown renderer. Every markdown path in the app renders
 * untrusted content through `v-html`, so anything that survives here executes:
 * model output and tool results in chat (a scraped page, an SSH command's
 * stdout), org-authored bodies in skills.
 *
 * There is deliberately one of these. It previously lived in `features/chat` and
 * `features/skills` kept a partial copy that closed the first hole below and not
 * the second, which is the drift a second copy of security-critical code always
 * produces.
 *
 * `marked` has had no `sanitize` option since v5 and passes raw HTML straight
 * through, so sanitizing is the caller's job. Two distinct holes have to be
 * closed, and closing only the first is the trap:
 *
 *  1. **Raw HTML in the source** — `<img src=x onerror=…>`. Handled by
 *     overriding the HTML-emitting renderer hooks to escape rather than emit.
 *  2. **Dangerous URLs in ordinary markdown** — `[click](javascript:alert(1))`.
 *     These never reach the `html` hook at all, because marked constructs the
 *     `<a>`/`<img>` itself from perfectly well-formed markdown. Only a scheme
 *     allowlist on the link and image renderers stops them.
 *
 * No new dependency: a full HTML sanitizer would be the more general answer, but
 * the output surface here is exactly what marked emits, which these hooks bound
 * completely.
 */
import hljs from 'highlight.js/lib/core';
import type { LanguageFn } from 'highlight.js';
import bash from 'highlight.js/lib/languages/bash';
import css from 'highlight.js/lib/languages/css';
import diff from 'highlight.js/lib/languages/diff';
import dockerfile from 'highlight.js/lib/languages/dockerfile';
import go from 'highlight.js/lib/languages/go';
import java from 'highlight.js/lib/languages/java';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import markdown from 'highlight.js/lib/languages/markdown';
import python from 'highlight.js/lib/languages/python';
import rust from 'highlight.js/lib/languages/rust';
import sql from 'highlight.js/lib/languages/sql';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import yaml from 'highlight.js/lib/languages/yaml';
import { Marked } from 'marked';

/**
 * Languages are registered individually rather than importing the full bundle,
 * which pulls in ~190 grammars for a chat that realistically shows a dozen.
 */
const LANGUAGES: Record<string, LanguageFn> = {
  bash,
  css,
  diff,
  dockerfile,
  go,
  java,
  javascript,
  json,
  markdown,
  python,
  rust,
  sql,
  typescript,
  xml,
  yaml,
};
for (const [name, fn] of Object.entries(LANGUAGES)) hljs.registerLanguage(name, fn);

/** Common spellings the model writes that are not the registered grammar name. */
const LANGUAGE_ALIASES: Record<string, string> = {
  js: 'javascript',
  jsx: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  py: 'python',
  rb: 'ruby',
  sh: 'bash',
  shell: 'bash',
  zsh: 'bash',
  console: 'bash',
  yml: 'yaml',
  html: 'xml',
  vue: 'xml',
  golang: 'go',
  rs: 'rust',
  md: 'markdown',
  docker: 'dockerfile',
};

function resolveLanguage(requested: string): string | null {
  const name = LANGUAGE_ALIASES[requested.toLowerCase()] ?? requested.toLowerCase();
  return hljs.getLanguage(name) ? name : null;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Schemes a link may carry. Anything else — `javascript:`, `data:`, `vbscript:`,
 * or an unknown scheme — is dropped rather than rewritten, so a novel scheme
 * fails closed instead of shipping.
 *
 * Relative and fragment URLs are allowed: they cannot introduce a scheme.
 */
const SAFE_LINK_SCHEMES = ['http:', 'https:', 'mailto:'];

/** Images additionally admit `data:image/*`, which cannot execute script. */
const SAFE_IMAGE_SCHEMES = ['http:', 'https:'];

function schemeOf(href: string): string | null {
  // Leading control characters and whitespace are stripped by browsers before
  // scheme resolution, so `java\nscript:alert(1)` still navigates as javascript:.
  // Matching them is the point of the check, not an oversight — the rule fires on the
  // escape form too, so the exemption is required, not cosmetic.
  // eslint-disable-next-line no-control-regex
  const cleaned = href.replace(/[\u0000-\u0020]/g, '').toLowerCase();
  const match = /^([a-z][a-z0-9+.-]*):/.exec(cleaned);
  return match ? match[1] + ':' : null;
}

function isSafeUrl(href: string, allowed: string[], allowDataImage = false): boolean {
  const scheme = schemeOf(href);
  if (scheme === null) return true; // relative, fragment, or protocol-relative path
  if (allowDataImage && /^data:image\/(png|jpe?g|gif|webp|avif);base64,/i.test(href.trim())) {
    return true;
  }
  return allowed.includes(scheme);
}

const safeMarked = new Marked({
  breaks: true,
  gfm: true,
  renderer: {
    html({ text }) {
      return escapeHtml(text);
    },
    link({ href, title, tokens }) {
      // `this.parser.parseInline` renders the label's own markdown; the label is
      // never trusted as HTML because its tokens route back through these hooks.
      const label = this.parser.parseInline(tokens);
      if (!isSafeUrl(href, SAFE_LINK_SCHEMES)) {
        // Drop the link, keep the words. Silently emitting a dead `<a>` would
        // hide from the reader that the model asked them to click something.
        return label;
      }
      const t = title ? ` title="${escapeHtml(title)}"` : '';
      // Model-authored links are external and untrusted: never let one reach
      // window.opener, and tell crawlers not to follow.
      return `<a href="${escapeHtml(href)}"${t} target="_blank" rel="noopener noreferrer nofollow">${label}</a>`;
    },
    image({ href, title, text }) {
      if (!isSafeUrl(href, SAFE_IMAGE_SCHEMES, true)) return escapeHtml(text);
      const t = title ? ` title="${escapeHtml(title)}"` : '';
      return `<img src="${escapeHtml(href)}" alt="${escapeHtml(text)}"${t} loading="lazy">`;
    },
    /**
     * Fenced code. The block carries its language as a data attribute (rendered
     * as a chip by CSS) and a copy button — both emitted here rather than by a
     * component because markdown produces the block, so there is nothing for a
     * component to wrap without re-parsing the HTML.
     *
     * The code text is still escaped, and the language is allowlisted to a short
     * identifier before it reaches an attribute: it comes from the model, so
     * `js" onload="…` must not be able to close the attribute.
     */
    code({ text, lang }) {
      const requested = (lang ?? '').trim().split(/\s+/)[0] ?? '';
      const language = /^[a-z0-9+#._-]{1,24}$/i.test(requested) ? requested : '';
      const langAttr = language ? ` data-lang="${escapeHtml(language)}"` : '';

      // Highlighting is the one place markup is emitted from model-supplied
      // text rather than escaped. It is safe because highlight.js escapes the
      // source itself and only ever adds its own <span class="hljs-*"> — but it
      // is only trusted for a *registered* grammar. An unknown language falls
      // back to plain escaping rather than `highlightAuto`, which would guess.
      const resolved = language ? resolveLanguage(language) : null;
      let body: string;
      if (resolved) {
        try {
          body = hljs.highlight(text, { language: resolved, ignoreIllegals: true }).value;
        } catch {
          body = escapeHtml(text);
        }
      } else {
        body = escapeHtml(text);
      }

      const codeClass = resolved ? ` class="hljs language-${escapeHtml(resolved)}"` : '';
      return (
        `<pre class="asab-code"${langAttr}>` +
        `<button type="button" class="asab-copy" data-copy aria-label="Copy code">Copy</button>` +
        `<code${codeClass}>${body}</code>` +
        `</pre>`
      );
    },
  },
});

/**
 * Renders assistant markdown to sanitized HTML.
 *
 * User text is deliberately not routed through here — there is no reason to
 * parse markdown in an echo of what the user just typed, and not parsing it is
 * one less surface.
 */
export function renderMarkdown(source: string): string {
  try {
    return safeMarked.parse(source, { async: false }) as string;
  } catch {
    // A parse failure must degrade to inert text, never to the raw source.
    return escapeHtml(source);
  }
}
